import { useContext, useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { gql } from 'graphql-request';

import { AppContext } from '../../App.context';
import { isNumeric, Issue, USER_ROLE } from '../../constants';
import config from '../../config';
import { useIssueNotFound } from '../../hooks/use-issue';

/**
 * React hook that returns the details of the issue
 *
 * @param id - Issue identifier
 *
 */
export function useGetIssue(id: string): GetIssueResponse {
	const isAdmin = localStorage.getItem(USER_ROLE) === 'admin';
	const { graphQLClient } = useContext(AppContext);
	const [issue, setIssue] = useState<Issue | null | undefined>();
	const cacheTime = isAdmin
		? 0
		: 1000 * 60 * (config.app?.cache?.ttl?.getIssue || 60);
	const { isPending, error, data } = useQuery<PreviewQuery>({
		queryKey: ['preview', id],
		queryFn: async () =>
			graphQLClient.request(previewQueryDocument, {
				id,
				isSlug: !isNumeric(id),
			}),
		staleTime: cacheTime,
		gcTime: cacheTime,
	});

	useIssueNotFound(issue);

	useEffect(() => {
		if (!data) return;
		setIssue(data.issue);
	}, [data]);

	return {
		isLoading: isPending,
		error: error ? new Error(error.message) : null,
		issue,
	};
}

interface GetIssueResponse {
	issue: any | null | undefined;
	isLoading: boolean;
	error?: Error | null;
}

interface PreviewQuery {
	issue: Issue | null;
}

export const previewQueryDocument = gql`
	query PreviewIssue($id: ID!, $isSlug: Boolean!) {
		issue(id: $id, isSlug: $isSlug) {
			id
			name
			cover
			externalUrl
			slug
			description
			features
			data
			updated
			isPaid
			created
			releaseDate
			articles {
				id
				ArticleID
				role
				name
				channel
				features
				index
				slug
				description
				isFeatured
				styleProperties
				updated
				created
			}
		}
	}
`;
